/**
 * ChatPanel — Real-time chat UI for DoubtQ Student App
 * Connects to DoubtQ-Backend via Socket.io and REST API.
 *
 * Props:
 *   orderId   {string} — the order's displayId (e.g. "ASN-001")
 *   onClose   {function} — called when user closes the panel
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axiosInstance from '../../axiosInterceptor';
import { initSocket, getSocket } from '../../utils/socket';
import { useChatNotification } from '../../context/ChatNotificationContext';
import './ChatPanel.scss';

const BASE_URL =
  process.env.REACT_APP_FILE_BASE_URL || 'https://api.doubtq.com';

const ChatPanel = ({ orderId, orderTitle, onClose, onOrderUpdate, orderCategory }) => {
  // Only show deadline logic for Assignment help orders
  const isAssignment = orderCategory === 'AssignmentHelp';
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [deadlineInput, setDeadlineInput] = useState('');
  const [submittingDeadline, setSubmittingDeadline] = useState(false);
  const [showDeadlinePicker, setShowDeadlinePicker] = useState(false);
  const [currentDeadline, setCurrentDeadline] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Student ID stored by checkToken.js after login/token-verify
  const studentId = localStorage.getItem('studentId');

  // ── Notification context — clear badge on join, suppress while open ───────
  const { markOrderRead, setActiveOrderId } = useChatNotification();

  // ── Helpers ──────────────────────────────────────────────────────────────

  const scrollToBottom = useCallback(() => {
    // Use rAF to wait for React to paint the DOM, then scroll
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 0);
    });
  }, []);

  // ── Fetch Messages + Order Details on Mount ────────────────────────────────

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await axiosInstance.get(
          `/student/chat/messages/${orderId}`
        );
        setMessages(res.data.messages || []);

        // Mark admin messages as read
        await axiosInstance.post(`/student/chat/messages/${orderId}/read`);
      } catch (err) {
        console.error('[ChatPanel] fetch messages error:', err);
      } finally {
        setLoading(false);
        // Scroll AFTER loading is done and React renders the messages
        scrollToBottom();
      }
    };

    const fetchOrderDetails = async () => {
      try {
        const res = await axiosInstance.get(`/student/chat/orders/${orderId}`);
        if (res.data?.order?.deadline) {
          setCurrentDeadline(res.data.order.deadline);
        }
      } catch (err) {
        console.error('[ChatPanel] fetch order details error:', err);
      }
    };

    fetchMessages();
    fetchOrderDetails();
  }, [orderId, scrollToBottom]);

  // ── Socket.io Listeners ───────────────────────────────────────────────────

  useEffect(() => {
    const token = localStorage.getItem('token');
    const socket = initSocket(token);
    if (!socket) return;

    // ── Critical: only emit join-order AFTER socket is confirmed connected ──
    let joined = false;

    const joinRoom = () => {
      if (!joined) {
        joined = true;
        socket.emit('join-order', { orderId });
        console.log('[Chat] join-order emitted for:', orderId);
        // Clear unread badge ONLY after we've joined the room
        markOrderRead(orderId);
        // Suppress incoming notifications for this order while panel is open
        setActiveOrderId(orderId);
      }
    };

    if (socket.connected) {
      joinRoom();
    } else {
      // Not yet connected — queue the join for when connection is ready
      socket.once('connect', joinRoom);
    }

    const handleNewMessage = (message) => {
      setMessages((prev) => {
        // Prevent duplicate messages by checking _id
        const id = message.id || message._id;
        if (id && prev.some(m => (m.id || m._id) === id)) return prev;
        return [...prev, message];
      });
      scrollToBottom();
      // Mark as read immediately since panel is open
      axiosInstance
        .post(`/student/chat/messages/${orderId}/read`)
        .catch(() => { });

      // Trigger re-fetch for deadline updates (System message)
      if (message.content && message.content.includes('Deadline') && onOrderUpdate) {
        onOrderUpdate();
      }
    };

    const handleUserTyping = ({ userRole, isTyping }) => {
      if (userRole === 'admin') {
        setTyping(isTyping);
      }
    };

    const handlePaymentRequest = (paymentMessage) => {
      setMessages((prev) => [...prev, paymentMessage]);
      scrollToBottom();
      if (onOrderUpdate) {
        onOrderUpdate();
      }
    };

    const handlePaymentConfirmed = () => {
      // Refresh messages to show updated payment status
      setTimeout(() => {
        axiosInstance
          .get(`/student/chat/messages/${orderId}`)
          .then((res) => {
            setMessages(res.data.messages || []);
            scrollToBottom();
          })
          .catch(() => { });
      }, 500);
      // Call onOrderUpdate for payment confirmations
      if (onOrderUpdate) {
        onOrderUpdate();
      }
    };

    socket.on('new-message', handleNewMessage);
    socket.on('user-typing', handleUserTyping);
    socket.on('payment-request', handlePaymentRequest);
    socket.on('payment-confirmed', handlePaymentConfirmed);

    return () => {
      // Remove queued join listener if panel unmounts before connection
      socket.off('connect', joinRoom);
      socket.emit('leave-order', { orderId });
      socket.off('new-message', handleNewMessage);
      socket.off('user-typing', handleUserTyping);
      socket.off('payment-request', handlePaymentRequest);
      socket.off('payment-confirmed', handlePaymentConfirmed);
      // Re-enable notifications for all orders when panel closes
      setActiveOrderId(null);
    };
  }, [orderId, scrollToBottom]);


  // Auto-scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // ── Typing Indicator ──────────────────────────────────────────────────────

  const handleTyping = () => {
    const socket = getSocket();
    if (!socket) return;

    socket.emit('typing-start', { orderId, userRole: 'student' });

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit('typing-stop', { orderId, userRole: 'student' });
    }, 2500);
  };

  // ── Send Text Message ─────────────────────────────────────────────────────

  const handleSendMessage = () => {
    if (!newMessage.trim() || sending) return;
    const socket = getSocket();
    if (!socket) return;

    setSending(true);
    socket.emit('send-message', {
      orderId,
      senderId: studentId,
      senderRole: 'student',   // ← MUST be 'student', never 'user'
      messageType: 'TEXT',
      content: newMessage.trim(),
    });

    setNewMessage('');
    setSending(false);

    // Clear typing timeout
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    socket.emit('typing-stop', { orderId, userRole: 'student' });
  };

  // ── Deadline Setting ──────────────────────────────────────────────────────

  const formatDeadline = (deadline) => {
    if (!deadline) return null;
    const date = new Date(deadline);
    return date.toLocaleString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const handleSetDeadline = async (e) => {
    e.preventDefault();
    if (!deadlineInput) return;

    setSubmittingDeadline(true);
    try {
      await axiosInstance.patch(`/student/chat/orders/${orderId}/deadline`, {
        deadline: deadlineInput,
      });

      const formattedDate = new Date(deadlineInput).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });

      const isUpdate = !!currentDeadline;

      const socket = getSocket();
      if (socket) {
        socket.emit('send-message', {
          orderId,
          senderId: studentId,
          senderRole: 'student',
          messageType: 'TEXT',
          content: isUpdate
            ? `📅 Deadline updated to ${formattedDate}`
            : `📅 Deadline set to ${formattedDate}`,
        });
      }

      setCurrentDeadline(deadlineInput);
      setShowDeadlinePicker(false);
      setDeadlineInput('');

      // Trigger re-fetch in the parent component
      if (onOrderUpdate) {
        onOrderUpdate();
      }
    } catch (err) {
      console.error('[ChatPanel] Error setting deadline:', err);
    } finally {
      setSubmittingDeadline(false);
    }
  };

  // ── File Upload ───────────────────────────────────────────────────────────

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      alert('File size must be less than 15MB');
      return;
    }

    setUploading(true);
    const socket = getSocket();

    try {
      const formData = new FormData();
      formData.append('file', file);

      // axiosInstance has baseURL: '/api' based on standard config
      const uploadRes = await axiosInstance.post('/student/chat/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const fileData = uploadRes.data.file;

      if (socket) {
        socket.emit('send-message', {
          orderId,
          senderId: studentId,
          senderRole: 'student',
          messageType: 'FILE',
          fileUrl: fileData.url,
          fileName: fileData.originalName,
          fileSize: fileData.size,
        });
      }
    } catch (err) {
      console.error('[ChatPanel] File upload error:', err);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // ── Payment Handling ──────────────────────────────────────────────────────

  const handlePayment = (messageId, paymentLink) => {
    window.open(paymentLink, '_blank');
    setTimeout(() => {
      const confirmed = window.confirm(
        'Click OK to confirm that your payment was successful.'
      );
      if (confirmed) {
        const socket = getSocket();
        if (socket) socket.emit('payment-success', { orderId, messageId });
      }
    }, 1000);
  };

  // ── File Download ─────────────────────────────────────────────────────────

  const handleFileDownload = async (fileUrl, fileName) => {
    try {
      const token = localStorage.getItem('token');

      // fileUrl already includes '/api/' so avoid double slashing
      // BASE_URL from .env might have a trailing slash, and fileUrl might have a leading slash
      const cleanBaseUrl = BASE_URL.endsWith('/') ? BASE_URL.slice(0, -1) : BASE_URL;
      const cleanFileUrl = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
      const downloadUrl = `${cleanBaseUrl}${cleanFileUrl}`;

      const res = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[ChatPanel] Download error:', err);
      alert('Failed to download file. Please try again.');
    }
  };

  // ── Currency Helper ───────────────────────────────────────────────────────

  const getCurrencySymbol = (currency) => {
    return `${currency || 'USD'} `;
  };

  // ── Render a Single Message ───────────────────────────────────────────────

  const renderMessage = (msg) => {
    const isStudent = msg.senderRole === 'student';
    const rowClass = `dq-chat-panel__msg-row dq-chat-panel__msg-row--${isStudent ? 'student' : 'admin'}`;

    const timestamp = (() => {
      const d = new Date(msg.createdAt || msg.timestamp);
      return !isNaN(d.getTime())
        ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '';
    })();

    return (
      <div key={msg.id || msg._id} className={rowClass}>
        <span className="dq-chat-panel__msg-label">
          {isStudent ? 'You' : 'Support'}
        </span>

        {msg.messageType === 'TEXT' && (
          <div
            className={`dq-chat-panel__bubble dq-chat-panel__bubble--${isStudent ? 'student' : 'admin'}`}
          >
            {msg.content}
          </div>
        )}

        {msg.messageType === 'FILE' && (
          <div
            className={`dq-chat-panel__bubble dq-chat-panel__bubble--${isStudent ? 'student' : 'admin'}`}
          >
            <div className="dq-chat-panel__file-msg">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width={20} height={20}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <div>
                <p className="dq-chat-panel__file-msg-name">{msg.fileName}</p>
                <button
                  className="dq-chat-panel__file-msg-download"
                  onClick={() => handleFileDownload(msg.fileUrl, msg.fileName)}
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        )}

        {msg.messageType === 'PAYMENT' && (
          <div className="dq-chat-panel__payment-card">
            <p className="dq-chat-panel__payment-card-title">💳 Payment Request</p>
            <p className="dq-chat-panel__payment-card-amount">
              {msg.paymentData?.amount || msg.paymentAmount} $
            </p>
            {msg.paymentData?.status === 'pending' ? (
              <button
                className="dq-chat-panel__pay-btn"
                onClick={() => {}}
                title="Payment is temporarily disabled"
                style={{ cursor: 'not-allowed', opacity: 0.7 }}
              >
                Pay Now
              </button>
            ) : (
              <span className="dq-chat-panel__paid-badge">✅ Payment Completed</span>
            )}
          </div>
        )}

        {timestamp && (
          <span className="dq-chat-panel__msg-time">{timestamp}</span>
        )}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="dq-chat-panel">
      {/* Header */}
      <div className="dq-chat-panel__header">
        <h6>💬 {orderTitle || `Order #${orderId}`}</h6>
        {onClose && (
          <button className="dq-chat-panel__close-btn" onClick={onClose}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Deadline Section — Only for Assignment help; top component when no deadline, tag+dropdown when exists */}
      {isAssignment && (!currentDeadline || showDeadlinePicker) && (
        <div className="dq-chat-panel__deadline-banner">
          <div className="dq-chat-panel__deadline-banner-inner">
            <div className="dq-chat-panel__deadline-title">
              📅 {currentDeadline ? 'Change Deadline' : 'Set Your Deadline'}
            </div>
            <p className="dq-chat-panel__deadline-desc">
              {currentDeadline
                ? `Current: ${formatDeadline(currentDeadline)}. Pick a new date below.`
                : 'Please set your deadline so we can prioritize your order.'}
            </p>
            <form className="dq-chat-panel__deadline-form" onSubmit={handleSetDeadline}>
              <input
                type="datetime-local"
                value={deadlineInput}
                onChange={(e) => setDeadlineInput(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                required
              />
              <div className="dq-chat-panel__deadline-actions">
                <button type="submit" disabled={submittingDeadline || !deadlineInput}>
                  {submittingDeadline ? 'Saving...' : currentDeadline ? 'Update Deadline' : 'Set Deadline'}
                </button>
                {currentDeadline && showDeadlinePicker && (
                  <button
                    type="button"
                    className="dq-chat-panel__deadline-cancel"
                    onClick={() => { setShowDeadlinePicker(false); setDeadlineInput(''); }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deadline Tag — shown when deadline exists and picker is not open (Assignment only) */}
      {isAssignment && currentDeadline && !showDeadlinePicker && (
        <div className="dq-chat-panel__deadline-tag-row">
          <button
            className="dq-chat-panel__deadline-tag"
            onClick={() => setShowDeadlinePicker(true)}
            title="Click to change deadline"
          >
            📅 {formatDeadline(currentDeadline)}
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="12" height="12">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="dq-chat-panel__messages" ref={messagesContainerRef}>
        {loading ? (
          <div className="dq-chat-panel__loading">
            <div className="dq-chat-panel__spinner" />
          </div>
        ) : messages.length === 0 ? (
          <div className="dq-chat-panel__empty">
            No messages yet. Start the conversation! 👋
          </div>
        ) : (
          messages.map(renderMessage)
        )}

        {/* Typing indicator */}
        {typing && (
          <div className="dq-chat-panel__typing">
            <span /><span /><span />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Upload progress */}
      {uploading && (
        <div className="dq-chat-panel__upload-bar">
          Uploading file…
        </div>
      )}

      {/* Input area */}
      <div className="dq-chat-panel__input-area">
        <div className="dq-chat-panel__input-row">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xlsx,.pptx,.zip"
          />

          {/* Attach button */}
          <button
            className="dq-chat-panel__attach-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            title="Attach file"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </button>

          {/* Text input */}
          <input
            type="text"
            className="dq-chat-panel__text-input"
            placeholder="Type a message…"
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
          />

          {/* Send button */}
          <button
            className="dq-chat-panel__send-btn"
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || sending}
            title="Send"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
